import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { HttpError } from 'app/helpers/httpError';
import { HttpRequestHelper } from 'app/helpers/httpRequestHelper';
import { DeleteGroupReq } from 'app/interfaces/requests/Delete/deleteGroupReq';
import { ExposedAddGroupsReq } from 'app/interfaces/requests/Exposed/exposedAddGroupsReq';
import { ExposedEditGroupReq } from 'app/interfaces/requests/Exposed/exposedEditGroupReq';
import { ExposedListGroupEntriesReq } from 'app/interfaces/requests/Exposed/exposedListGroupEntriesReq';
import { ExposedReadGroupReq } from 'app/interfaces/requests/Exposed/exposedReadGroupReq';
import { ExposedReadGroupRes } from 'app/interfaces/requests/Exposed/exposedReadGroupRes';
import { ListGroupEntriesRes } from 'app/interfaces/requests/List/listGroupEntriesRes';
import { ListGroupTypesRes } from 'app/interfaces/responses/List/listGroupTypesRes';
import { ListGroupsRes } from 'app/interfaces/responses/List/listGroupsRes';
import { PagedEntriesRes } from 'app/interfaces/responses/List/pagedEntriesRes';
import { HttpStatus } from 'app/interfaces/support/status/httpStatus';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { environment } from 'environments/environment';
import { Observable, Subject, catchError, finalize, map, of } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class GroupsService {
    private httpRequestHelper = new HttpRequestHelper();

    constructor(
        private http: HttpClient,
        private loaderService: LoaderService,
        private alertsService: AlertsService,
        private router: Router,
        public dialog: MatDialog
    ) {}

    public readGroup(req: ExposedReadGroupReq): Observable<ExposedReadGroupRes> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki upplýsingar');

        let subject = new Subject<ExposedReadGroupRes>();
        this.http
            .get(environment.localApi + 'groups/readGroup/' + req.groupId, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ExposedReadGroupRes>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public listGroupTypes(showError = true): Observable<Array<ListGroupTypesRes>> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki upplýsingar');

        let subject = new Subject<Array<ListGroupTypesRes>>();
        this.http
            .get(environment.localApi + 'groups/listGroupTypes', this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<Array<ListGroupTypesRes>>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public readGroupMetadata(req: ExposedReadGroupReq): Observable<ExposedReadGroupRes> {
        return this.http
            .get<
                HttpStatus<ExposedReadGroupRes>
            >(`${environment.localApi}groups/metadata/${req.groupId}`, this.httpRequestHelper.getRequestOptionsAuth())
            .pipe(map((res) => res.returnItem));
    }

    public listUserGroups(showError = true): Observable<Array<ListGroupsRes>> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki upplýsingar');

        let subject = new Subject<Array<ListGroupsRes>>();
        this.http
            .get(environment.localApi + 'groups/listUserGroups', this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<Array<ListGroupsRes>>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public addGroup(showError = true, req: ExposedAddGroupsReq): Observable<number> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Skrái hóp');

        let subject = new Subject<number>();
        this.http
            .post(environment.localApi + 'groups/addGroup', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<number>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage("Hópur '" + req.Name + "' hefur verið stofnaður");
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                    subject.next(-1);
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public deleteGroup(showError = true, req: DeleteGroupReq): Observable<boolean> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Eyði hóp');

        let subject = new Subject<boolean>();
        this.http
            .post(environment.localApi + 'groups/deleteGroup', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<object>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage('Hópi hefur verið eytt');
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(true);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                    subject.next(false);
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public editGroup(showError = true, req: ExposedEditGroupReq): Observable<boolean> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Uppfæri hóp');

        let subject = new Subject<boolean>();
        this.http
            .post(environment.localApi + 'groups/editGroup', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<object>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage("Hópur '" + req.Name + "' hefur verið uppfærður");
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(true);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                    subject.next(false);
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public readGroupEntries(req: ExposedListGroupEntriesReq): Observable<PagedEntriesRes<ListGroupEntriesRes>> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki upplýsingar');
        return this.http
            .post<
                HttpStatus<PagedEntriesRes<ListGroupEntriesRes>>
            >(`${environment.localApi}groups/readGroupEntries`, req, this.httpRequestHelper.getRequestOptionsAuth())
            .pipe(
                map((r) => r.returnItem),
                finalize(() => this.loaderService.changeActiveLoaders(-1)),
                catchError((err) => {
                    HttpError(this.alertsService, this.router, err, '', '', '', '');
                    return of({ items: [], totalCount: 0 } as PagedEntriesRes<ListGroupEntriesRes>);
                })
            );
    }
}
